/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';

import { getPasswordInputSchema } from 'schema';
import { resetPassword } from 'actions/app.actions';
import { InputFieldWithLabel, PrimaryButton } from 'components';
import { ROUTES } from '../../constants';
import {
  ButtonContainer,
  InputsContainer,
  LinkContainer,
} from './ResetPasswordForm.styles';
import { ICustomTheme } from 'interfaces';
import { useTheme } from '@material-ui/core';
import { LinkWithUIGuidelines } from 'component-library/atoms/LinkWithUIGuidelines';

type InputFormTypes = {
  loading: boolean;
  token: string;
};

type InitialValuesTypes = {
  confirmPassword: string;
  email: string;
  password: string;
};

const initialValues: InitialValuesTypes = {
  confirmPassword: '',
  email: '',
  password: '',
};

const ResetPasswordForm = ({ loading, token }: InputFormTypes): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  const passwordValidator = getPasswordInputSchema(t);

  const formSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email(t('core.invalid_email_address'))
      .required(t('core.required')),
    password: passwordValidator,
    confirmPassword: passwordValidator.oneOf(
      [Yup.ref('password')],
      t('core.password.mismatch'),
    ),
  });

  const handleSubmit = ({
    confirmPassword, // eslint-disable-line @typescript-eslint/no-unused-vars
    email,
    ...otherFields
  }: InitialValuesTypes) => {
    dispatch(
      resetPassword.request(
        { token, email: email.trim(), ...otherFields },
        history,
      ),
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      onSubmit={handleSubmit}
    >
      <Form>
        <InputsContainer>
          <InputFieldWithLabel
            label={t('auth.global.email')}
            name="email"
            placeholder={t('auth.global.email_placeholder')}
            required
          />

          <InputFieldWithLabel
            label={t('auth.global.new_password')}
            name="password"
            placeholder={t('auth.global.new_password_placeholder')}
            required
            textCase="sentence"
            type="password"
          />

          <InputFieldWithLabel
            label={t('auth.global.confirm_password')}
            name="confirmPassword"
            placeholder={t('auth.global.confirm_password_placeholder')}
            required
            textCase="sentence"
            type="password"
          />
        </InputsContainer>

        <LinkContainer>
          <LinkWithUIGuidelines
            isInternalLink
            url={ROUTES.LOGIN}
            color={theme.colors.paletteFromBO.main}
            textLink={t('auth.reset_password.return')}
            fontWeight="bold"
            withUnderline
          />
        </LinkContainer>

        <div
          css={css`
            text-align: center;
          `}
        >
          <ButtonContainer>
            <PrimaryButton
              label={t('auth.reset_password.submit')}
              isDisabled={loading}
              isLoading={loading}
            />
          </ButtonContainer>
        </div>
      </Form>
    </Formik>
  );
};

export default ResetPasswordForm;
